import { Close, Email, Verified } from "@mui/icons-material";
import { CircularProgress, Dialog, IconButton } from "@mui/material";
import { collection, getDocs, query, where } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { db } from "../../firebase";

const GOLD = "#ffc000";
const PRIMARY = "#000c66";

export const AuthorModal = ({ open, onClose, authorName }) => {
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !authorName) return;
    setAuthor(null);
    setLoading(true);
    const fetch = async () => {
      try {
        const q = query(
          collection(db, "authors"),
          where("name", "==", authorName),
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          setAuthor({ _id: d.id, ...d.data() });
        }
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [open, authorName]);

  const isApproved = author?.status === "approved";

  const joinedDate = (() => {
    const raw = author?.createdAt;
    if (!raw) return null;
    const d = raw.toDate ? raw.toDate() : new Date(raw);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          background: "transparent",
          boxShadow: "0 24px 60px rgba(0,12,102,0.25)",
        },
      }}
    >
      {/* Header band */}
      <div
        style={{ background: PRIMARY, position: "relative", paddingBottom: 60 }}
      >
        {/* Close */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "rgba(255,255,255,0.6)",
            zIndex: 2,
          }}
        >
          <Close fontSize="small" />
        </IconButton>

        {/* Watermark */}
        <span
          style={{
            position: "absolute",
            right: -10,
            top: "50%",
            transform: "translateY(-50%)",
            fontFamily: "Aref Ruqaa, serif",
            fontSize: 80,
            color: GOLD,
            opacity: 0.06,
            lineHeight: 1,
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          AUTHOR
        </span>

        {/* Photo */}
        <div
          style={{ display: "flex", justifyContent: "center", paddingTop: 32 }}
        >
          <div style={{ position: "relative", width: 140, height: 140 }}>
            {loading ? (
              <div
                style={{
                  width: 140,
                  height: 140,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,192,0,0.08)",
                  border: `3px solid ${GOLD}`,
                }}
              >
                <CircularProgress size={32} sx={{ color: GOLD }} />
              </div>
            ) : author?.imgSrc ? (
              <img
                src={author.imgSrc}
                alt={author.name}
                style={{
                  width: 140,
                  height: 140,
                  objectFit: "cover",
                  display: "block",
                  borderRadius: "50%",
                  border: `3px solid ${GOLD}`,
                  boxShadow: `0 0 0 4px ${GOLD}33`,
                }}
              />
            ) : (
              <svg
                viewBox="0 0 140 140"
                style={{
                  width: 140,
                  height: 140,
                  borderRadius: "50%",
                  background: "rgba(255,192,0,0.08)",
                  border: `3px solid ${GOLD}`,
                  boxShadow: `0 0 0 4px ${GOLD}33`,
                }}
                fill="none"
              >
                <circle cx="70" cy="52" r="26" fill={GOLD} opacity="0.8" />
                <path
                  d="M14 126c0-30.928 25.072-56 56-56s56 25.072 56 56"
                  stroke={GOLD}
                  strokeWidth="7"
                  strokeLinecap="round"
                  opacity="0.8"
                />
              </svg>
            )}

            {/* Verified badge */}
            {isApproved && (
              <div
                style={{
                  position: "absolute",
                  bottom: 4,
                  right: 4,
                  background: PRIMARY,
                  borderRadius: "50%",
                  padding: 4,
                  border: `2px solid ${GOLD}`,
                  zIndex: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Verified style={{ color: GOLD, fontSize: 20 }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div
        style={{
          background: "#fff",
          padding: "40px 24px 24px",
          marginTop: -24,
          borderRadius: "24px 24px 0 0",
          position: "relative",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "24px 0",
            }}
          >
            <CircularProgress size={24} sx={{ color: PRIMARY }} />
          </div>
        ) : !author ? (
          <p
            style={{
              fontFamily: "Abel",
              textAlign: "center",
              color: "#aaa",
              fontSize: 14,
            }}
          >
            Author profile not found.
          </p>
        ) : (
          <>
            {/* Name + bio */}
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                <h2
                  style={{
                    fontFamily: "Aref Ruqaa, serif",
                    fontSize: 22,
                    fontWeight: 700,
                    color: PRIMARY,
                    margin: 0,
                  }}
                >
                  {author.name}
                </h2>
              </div>
              {author.bio && (
                <p
                  style={{
                    fontFamily: "Abel",
                    fontSize: 13,
                    color: GOLD,
                    marginTop: 4,
                    letterSpacing: "0.05em",
                  }}
                >
                  {author.bio}
                </p>
              )}
              {isApproved && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    marginTop: 6,
                    background: "rgba(255,192,0,0.12)",
                    border: `1px solid ${GOLD}44`,
                    borderRadius: 20,
                    padding: "2px 10px",
                    fontFamily: "Abel",
                    fontSize: 11,
                    color: GOLD,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  <Verified style={{ fontSize: 12, color: GOLD }} />
                  Verified Author
                </span>
              )}
            </div>

            {/* Divider */}
            <div
              style={{
                height: 1,
                background: `linear-gradient(90deg, transparent, ${GOLD}66, transparent)`,
                margin: "12px 0",
              }}
            />

            {/* Description */}
            {author.description && (
              <p
                style={{
                  fontFamily: "Abel",
                  fontSize: 14,
                  color: "#555",
                  lineHeight: 1.7,
                  marginBottom: 16,
                  textAlign: "center",
                  fontStyle: "italic",
                }}
              >
                "{author.description}"
              </p>
            )}

            {/* Contact items */}
            {/* <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {author.email && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: `${GOLD}18`,
                      border: `1px solid ${GOLD}33`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Email style={{ fontSize: 14, color: GOLD }} />
                  </div>
                  <span
                    style={{ fontFamily: "Abel", fontSize: 13, color: "#444" }}
                  >
                    {author.email}
                  </span>
                </div>
              )}
            </div> */}

            {/* Joined date */}
            {joinedDate && (
              <p
                style={{
                  fontFamily: "Abel",
                  fontSize: 11,
                  color: "#bbb",
                  textAlign: "center",
                  marginTop: 16,
                }}
              >
                Joined {joinedDate}
              </p>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
